import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
// import { useSelector } from 'react-redux';
import { Container } from '../layout/Container';
import { HeaderSection } from '../layout/CardsContainer';
import Button from '../components/Button';
import NavTab from '../components/NavTab';
import ENV_CONFIG from '../config/environment-unified';
import useTranslation from '../hooks/useTranslation';

const Events = () => {
  const { t } = useTranslation();
  // const isLoggedIn = useSelector(state => state.user.isLoggedIn);
  const [events, setEvents] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredEvents, setFilteredEvents] = useState([]);
  const [liveEventIds, setLiveEventIds] = useState(() => new Set());

  // Fetch categories and events
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        const normalizeArrayResponse = (data) => {
          if (Array.isArray(data)) return data;
          if (data && Array.isArray(data.data)) return data.data;
          if (data && Array.isArray(data.lives)) return data.lives;
          if (data && Array.isArray(data.streams)) return data.streams;
          return [];
        };

        const [categoriesResponse, eventsResponse, streamsResponse] = await Promise.all([
          axios.get(`${ENV_CONFIG.EVENT_CATEGORIES_ENDPOINT}`, { withCredentials: true }),
          axios.get(`${ENV_CONFIG.EVENTS_ENDPOINT}/events`, { withCredentials: true }),
          axios.get(`${ENV_CONFIG.STREAMS_ENDPOINT}`, { withCredentials: true }).catch(() => ({ data: [] }))
        ]);

        console.log('📋 Categories loaded:', categoriesResponse.data);
        console.log('🎉 Events loaded:', eventsResponse.data);

        // Tournaments have their own dedicated page (/events/tournaments), so exclude them here.
        const cats = (categoriesResponse.data.categories || []).filter(c => c?.slug !== 'tournament');
        const evs = (eventsResponse.data.events || []).filter(e => e?.category?.slug !== 'tournament');

        setCategories(cats);
        setEvents(evs);
        setFilteredEvents(evs);

        // Build a Set of event IDs that are currently live (based on stream-service currentEventId).
        const streams = normalizeArrayResponse(streamsResponse?.data);
        const liveIds = new Set();
        for (const s of streams) {
          const id = s?.currentEventId;
          if (!id) continue;
          // The streams endpoint is expected to return live streams only, but be defensive anyway.
          if (s?.isLive === false) continue;
          liveIds.add(String(id));
        }
        setLiveEventIds(liveIds);

      } catch (error) {
        console.error('❌ Error fetching events data:', error);
        toast.error(t('events.failed_to_load', 'Failed to load events'));
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [t]);

  // Keep live statuses fresh (events can flip from Pending -> Live while the page is open).
  useEffect(() => {
    let mounted = true;
    let timer = null;

    const normalizeArrayResponse = (data) => {
      if (Array.isArray(data)) return data;
      if (data && Array.isArray(data.data)) return data.data;
      if (data && Array.isArray(data.lives)) return data.lives;
      if (data && Array.isArray(data.streams)) return data.streams;
      return [];
    };

    async function refreshLiveIds() {
      try {
        const res = await axios.get(`${ENV_CONFIG.STREAMS_ENDPOINT}`, { withCredentials: true });
        const streams = normalizeArrayResponse(res?.data);
        const liveIds = new Set();
        for (const s of streams) {
          const id = s?.currentEventId;
          if (!id) continue;
          if (s?.isLive === false) continue;
          liveIds.add(String(id));
        }
        if (mounted) setLiveEventIds(liveIds);
      } catch {
        // ignore polling failures
      }
    }

    // Initial refresh (in case the initial Promise.all fallback swallowed a transient error)
    refreshLiveIds();
    timer = setInterval(refreshLiveIds, 15000);

    return () => {
      mounted = false;
      if (timer) clearInterval(timer);
    };
  }, [t]);

  // Filter events based on category and search
  useEffect(() => {
    let filtered = events;

    // Filter by category
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(event => event.category?.slug === selectedCategory);
    }

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(event =>
        event.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        event.description?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredEvents(filtered);
  }, [events, selectedCategory, searchTerm]);

  // Create tab list for categories
  const categoryTabs = [t('events.all'), ...categories.map(cat => cat.name)];
  const categoryValues = ['all', ...categories.map(cat => cat.slug)];

  const handleCategoryChange = (index) => {
    // NavTab uses 1-based index
    setSelectedCategory(categoryValues[index - 1]);
  };

  if (loading) {
    return (
      <Container style={{ paddingTop: '4rem', paddingBottom: '2rem' }}>
        <HeaderSection title={t('events.events')}>
          <div>{t('events.loading')}</div>
        </HeaderSection>
      </Container>
    );
  }

  return (
    <Container style={{ paddingTop: '4rem', paddingBottom: '2rem' }}>
      <HeaderSection title={t('events.events')}>
        <Link to="/events/create">
          <Button variant="primary" size="small">
            {t('events.create_event')}
          </Button>
        </Link>
      </HeaderSection>

      {/* Search Bar */}
      <SearchSection>
        <SearchInput
          type="text"
          placeholder={t('events.search_events')}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </SearchSection>

      {/* Category Tabs */}
      <NavTab
        list={categoryTabs}
        index={categoryValues.indexOf(selectedCategory) + 1}
        setIndex={handleCategoryChange}
      />

      {/* Events Grid */}
      <EventsGrid>
        {filteredEvents.length === 0 ? (
          <NoEventsMessage>
            <h3>{t('events.no_events_found')}</h3>
            <p>
              {searchTerm || selectedCategory !== 'all'
                ? t('events.adjust_filters')
                : t('events.be_first_create')}
            </p>
            <Link to="/events/create">
              <Button variant="primary">{t('events.create_event')}</Button>
            </Link>
          </NoEventsMessage>
        ) : (
          filteredEvents.map((event) => (
            <EventCard key={event.id} event={event} liveEventIds={liveEventIds} />
          ))
        )}
      </EventsGrid>
    </Container>
  );
};

// Event Card Component
const EventCard = ({ event, liveEventIds }) => {
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const { t } = useTranslation();

  const computeStatus = () => {
    const status = event?.status;
    const now = new Date();
    const parseDate = (value) => {
      if (!value) return null;
      const d = new Date(value);
      return Number.isNaN(d.getTime()) ? null : d;
    };
    const start = parseDate(event?.startDate);
    const end = parseDate(event?.endDate);
    const hasStarted = !!start && start.getTime() <= now.getTime();
    const hasEnded = !!end && end.getTime() <= now.getTime();

    const isLiveByStream = (() => {
      try {
        return !!liveEventIds?.has?.(String(event?.id));
      } catch {
        return false;
      }
    })();

    // If stream-service says this event is currently live, prefer "Live" even if event.status is still "published".
    if (isLiveByStream) return { key: 'live', label: t('events.status_live', 'Live'), color: '#F59E0B' };
    if (status === 'active') return { key: 'live', label: t('events.status_live', 'Live'), color: '#F59E0B' };
    if (status === 'completed') return { key: 'finished', label: t('events.status_ended', 'Finished'), color: '#6B7280' };
    if (status === 'cancelled') return { key: 'cancelled', label: t('events.status_cancelled', 'Cancelled'), color: '#EF4444' };

    // Derive display status from schedule when backend status isn't updated.
    // This fixes events stuck in "published" long after they ended.
    if (hasEnded) {
      return { key: 'finished', label: t('events.status_ended', 'Finished'), color: '#6B7280' };
    }
    if (start && start.getTime() > now.getTime()) {
      return { key: 'coming_soon', label: t('events.status_coming_soon', 'Coming soon'), color: '#10B981' };
    }
    if (hasStarted) {
      return { key: 'open', label: t('events.status_open', 'Open'), color: '#3B82F6' };
    }

    // Fallbacks (unknown dates, drafts, etc.)
    if (status === 'published') {
      return { key: 'pending', label: t('events.status_pending', 'Pending'), color: '#3B82F6' };
    }
    return { key: status || 'unknown', label: status ? status : t('events.status_unknown', 'Unknown'), color: '#6B7280' };
  };
  const computed = computeStatus();

  return (
    <StyledEventCard>
      <Link to={`/events/${event.slug}`}>
        <EventCardHeader $url={event.bannerImage || null}>
          <EventBadge color={event.category?.color || '#6B7280'}>
            {event.category?.icon} {event.category?.name}
          </EventBadge>
          <EventStatus color={computed.color}>
            {computed.label}
          </EventStatus>
        </EventCardHeader>

        <EventCardBody>
          <EventTitle>{event.name}</EventTitle>
          <EventDescription>
            {event.description?.substring(0, 120)}
            {event.description?.length > 120 && '...'}
          </EventDescription>

          <EventMeta>
            <EventDate>📅 {formatDate(event.startDate)}</EventDate>
            <EventParticipants>
              👥 {event.currentParticipants}
              {event.maxParticipants && ` / ${event.maxParticipants}`}
            </EventParticipants>
          </EventMeta>

          {event.ticketPrice > 0 && (
            <EventPrice>💰 {event.ticketPrice} {t('common.gcoins', 'G-Coins')}</EventPrice>
          )}
        </EventCardBody>
      </Link>

      <EventCardActions>
        {computed.key === 'live' && (
          <Link to={`/events/${event.slug}/watch`}>
            <Button variant="primary" size="small">{t('events.watch', 'Watch')}</Button>
          </Link>
        )}
        <Link to={`/events/${event.slug}`}>
          <Button variant="secondary" size="small">{t('events.details', 'Details')}</Button>
        </Link>
      </EventCardActions>
    </StyledEventCard>
  );
};

// Styled Components
const SearchSection = styled.div`
  margin: 2rem 0;
  display: flex;
  justify-content: center;
`;

const SearchInput = styled.input`
  width: 100%;
  max-width: 500px;
  padding: 12px 20px;
  border: 2px solid ${props => props.theme.border};
  border-radius: 25px;
  font-size: 16px;
  background: ${props => props.theme.body};
  color: ${props => props.theme.text};
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.primary};
  }
  
  &::placeholder {
    color: ${props => props.theme.textSecondary};
  }
`;

const EventsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 2rem;
  margin-top: 2rem;
`;

const NoEventsMessage = styled.div`
  grid-column: 1 / -1;
  text-align: center;
  padding: 4rem 2rem;
  background: ${props => props.theme.cardBg};
  border-radius: 12px;
  border: 1px solid ${props => props.theme.border};
  
  h3 {
    color: ${props => props.theme.text};
    margin-bottom: 1rem;
  }
  
  p {
    color: ${props => props.theme.textSecondary};
    margin-bottom: 2rem;
  }
`;

const StyledEventCard = styled.div`
  background: ${props => props.theme.cardBg};
  border-radius: 12px;
  border: 1px solid ${props => props.theme.border};
  overflow: hidden;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
    border-color: ${props => props.theme.primary};
  }
  
  a {
    text-decoration: none;
    color: inherit;
  }
`;

const EventCardHeader = styled.div`
  position: relative;
  height: 200px;
  background: ${({ $url }) =>
    $url
      ? `linear-gradient(180deg, rgba(0,0,0,0.10), rgba(0,0,0,0.55)), url(${$url}) center/cover no-repeat`
      : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const EventCardActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 12px 14px 14px;
  border-top: 1px solid ${({ theme }) => theme.border};
`;

const EventBadge = styled.div`
  position: absolute;
  top: 12px;
  left: 12px;
  background: ${props => props.color};
  color: white;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  z-index: 2;
`;

const EventStatus = styled.div`
  position: absolute;
  top: 12px;
  right: 12px;
  background: ${props => props.color};
  color: white;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  z-index: 2;
`;

const EventCardBody = styled.div`
  padding: 1.5rem;
`;

const EventTitle = styled.h3`
  color: ${props => props.theme.text};
  margin: 0 0 0.5rem 0;
  font-size: 1.25rem;
  font-weight: 600;
`;

const EventDescription = styled.p`
  color: ${props => props.theme.textSecondary};
  margin: 0 0 1rem 0;
  line-height: 1.5;
`;

const EventMeta = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
`;

const EventDate = styled.div`
  color: ${props => props.theme.textSecondary};
  font-size: 14px;
`;

const EventParticipants = styled.div`
  color: ${props => props.theme.textSecondary};
  font-size: 14px;
`;

const EventPrice = styled.div`
  color: ${props => props.theme.primary};
  font-weight: 600;
  font-size: 16px;
`;

export default Events;
